/**
 * AI 발주 채팅 모달 (BS self-contained).
 *
 * 호출:
 *   $j.Ui.BS.open({
 *       target:      '$j.F.Baljoo.ModalAiChat',
 *       fixedHeight: true,
 *       headerBg:    'gray',
 *   });
 *
 * 흐름:
 *   사용자가 자연어 입력 → POST /m/ajax/bal/ai/extract → 추출된 필드 누적
 *   서버가 quickReplies 를 주면 입력창 위 도크에 버튼으로 깔아 즉시 선택(네트워크 호출 없음)
 *   필수값이 다 차면 대화 안에 요약 카드 → CTA → POST /m/ajax/bal/ai/commit
 *   → 세션 저장 → location.replace('/m/bal?mode=ai')
 *
 * 화면 구성 (기획 시안 .claude/skills/jang-renewal-design/assets/prototype-bal-ai.html):
 *   .ai-topbar  「자주 발주」·「처음부터」  ← BS 헤더에 못 넣는다(.header button 이 전부 우상단 absolute)
 *   .ai-log     대화 + 완료 시 요약 카드
 *   .ai-dock    선택지 — 입력창 바로 위 고정, 칩은 가로 스와이프
 *   .ai-gauge   진행 게이지 + 오늘 사용량
 *   .ai-form    입력창 + 전송
 *
 * 스타일 정본은 resources/sass/mobile/features/_ai-chat.scss (라이트·다크 한 벌).
 */
import Util from '../../../Common/Util';

export default class AiChatModal {
    constructor() {
        this.id = null;
        this.parent = null;
        this.data = null;

        this.chatHistory = [];    // [{role:'user'|'model', text}]
        this._editingSlot = null; // 요약 카드에서 고치는 중인 슬롯 key (없으면 null)
        this._editBackup  = null; // 편집 시작 시점의 값 — 취소하면 되돌린다
        this.currentFields = {};  // 폼 프리필용 raw 값 (예: car_range="150|90~90")
        this.currentDisplay = {}; // 칩 표시용 라벨 (예: car_range="25톤이상 (90m)")

        this.patterns = [];             // 자주 발주한 조합 목록 (/suggest)
        this.sosokReq = null;           // {type:'samsung', title} — 회사별 필수 입력 요구 (머큐리 보험건은 발주폼이 받는다)
        this.needPayment = false;       // 결제방식 사전 선택 필요 여부
        this._awaitingMap = false;      // 지도 모달을 이 챗봇에서 열었는지 — callbackAddress 결과를 가져갈 주체 판별
        this._awaitingMemo = false;     // 다음 입력을 메모로 받는 중인지
        // 필드 → 그 값을 뽑아준 AI 호출의 계측 행번호. 사용자가 그 필드를 고치면 그 행에
        // user_modified 를 세운다 (정확도 지표 — app/Services/Ai/README.md 「계측」).
        this._aiFieldCall = {};
        this._lowConfTries = 0;         // 확신도가 낮아 되물은 횟수 (기획: 최대 2회)
        this._quickReplies = [];        // 서버가 준 마지막 선택지 (도크 재렌더용)
        this._freqSheet = null;         // 「자주 발주」 시트 핸들 ($j.Ui.Modal)
    }

    init() {
        this.parent?.setTitle?.('🤖 AI 발주 도우미');
        this._render();
        this._bind();
        this._prepareSosokRequirement();
        this._preparePayment();
        this._fetchPatterns();
        this._loadExamples();
        this._renderGauge();
        this._renderDock();
        this._syncChatLock();
    }

    destroy() {
        $('#' + this.id).off();
        this._freqSheet?.close?.();
        this._freqSheet = null;
    }

    // ── 라벨 (DB 컬럼 → 표시) ─────────────────────────────────────────────
    static get LABEL() {
        return {
            car_type: '장비',
            car_range: '스펙',
            car_option: '옵션',
            car_workhour: '작업량',
            car_workdaytype: '단위',
            work_day: '날짜',
            work_time: '시간',
            contract_address: '주소',
            howtopay: '결제',
            sign_howlong_int: '결제기간',
            contract_message: '메모',
            bonsa_col: '수임번호',
            contract_fieldnumber: '현장담당자',
        };
    }
    static get REQUIRED() {
        return ['car_type','car_range','car_workhour','work_day','work_time','contract_address'];
    }

    // ─────────────────────────────────────────────────────────────────────
    // 슬롯 — 진행 게이지·요약 카드가 세는 단위
    //
    // 서버 필드와 1:1 이 아니다. 사람은 "장비"를 하나로 인식하는데 서버는 차종(car_type)과
    // 스펙(car_range) 두 칸이라, 게이지를 필드 수로 세면 장비만 골라도 2칸이 차 버린다.
    // 회사별 필수값(수임번호·접수번호)과 결제방식도 이 회원에게 해당될 때만 칸이 된다.
    // ─────────────────────────────────────────────────────────────────────
    _slots() {
        const list = [];

        if (this.needPayment)                     list.push({ key: 'pay',     label: '결제방식' });
        if (this.sosokReq?.type === 'samsung')    list.push({ key: 'suim',    label: '수임번호', fields: ['bonsa_col'] });

        list.push(
            { key: 'gear', label: '장비',     fields: ['car_type', 'car_range'] },
            { key: 'addr', label: '주소',     fields: ['contract_address'] },
            { key: 'dur',  label: '작업소요', fields: ['car_workhour'] },
            { key: 'date', label: '날짜',     fields: ['work_day'] },
            { key: 'time', label: '시간',     fields: ['work_time'] },
        );
        return list;
    }

    _slotFilled(slot) {
        if (slot.key === 'pay')     return !!this.currentFields.howtopay;
        return slot.fields.every((f) => this.currentFields[f] != null && this.currentFields[f] !== '');
    }

    /** 슬롯에 채워진 값을 사람이 읽는 한 줄로. 요약 카드에 쓴다. */
    _slotText(slot) {
        if (slot.key === 'pay')     return this.currentDisplay.howtopay || G5['HOW_TO_PAY']?.[this.currentFields.howtopay] || '';

        const parts = slot.fields.map((f) => {
            const v = (this.currentDisplay[f] != null && this.currentDisplay[f] !== '')
                ? this.currentDisplay[f] : this.currentFields[f];
            return v == null ? '' : String(v);
        }).filter((v) => v !== '');

        // 작업량은 단위가 별도 필드다 — "2" 가 아니라 "2시간" 으로 보여야 한다
        if (slot.key === 'dur') {
            return this._workText(parts.join(' '), this.currentFields.car_workdaytype);
        }
        return parts.join(' ');
    }

    /**
     * 작업량 → 사람이 읽는 한 마디. 서버 BalAiController::formatWorkUnit 과 같은 규칙이다.
     *
     * 숫자에 단위를 무조건 붙이면 "4오전" 이 나온다. 오전·오후는 세는 단위가 아니라 작업 형태라
     * 앞의 숫자가 개수를 뜻하지 않는다(같은 '오전'에 1 인 행과 4 인 행이 섞여 있다).
     */
    _workText(hour, unit) {
        if (hour === '' || hour == null) return '';
        switch (unit) {
            case '오전': return '오전 반나절';
            case '오후': return '오후 반나절';
            case '야간': return `야간 ${hour}시간`;
            case '월':   return `${hour}개월`;
            case '일':   return `${hour}일`;
            case '시간':
            case undefined:
            case null:
            case '':     return `${hour}시간`;
            // 옛 데이터에 '톤' 같은 값이 남아 있다 — 붙여 쓰면 스펙으로 오독되므로 띄운다
            default:     return `${hour} ${unit}`;
        }
    }

    _render() {
        $('#' + this.id).html(`
<!-- 아이콘을 쓰지 않는다 — fa-clock-rotate-left/fa-rotate-left 가 이 환경에서 빈 네모로 떨어졌다.
     (FA 6.1.0 CDN 에 정의는 있는데 글리프가 안 나옴) 글자만으로 뜻이 통하는 버튼이라 텍스트로 둔다. -->
<div class="ai-topbar" style="display:none;">
    <button type="button" class="ai-top-btn ai-freq" aria-haspopup="dialog">자주 발주</button>
    <button type="button" class="ai-top-btn ai-restart" aria-label="처음부터 다시 시작">처음부터</button>
</div>
<div class="ai-log" role="log" aria-live="polite">
    <div class="ai-hint">
        자연어로 발주 내용을 알려주세요.<br>
        AI가 정리해서 발주 폼에 자동으로 채워드립니다.<br>
        상담 내용을 그대로 붙여넣어도 됩니다.
        <div class="examples">
            <span class="ex">내일 오전 10시 강남역 스카이 25톤 2시간</span>
            <span class="ex">모레 14시 잠실역 사다리 5층 1시간</span>
        </div>
    </div>
</div>
<div class="ai-dock" aria-label="선택지" style="display:none;"></div>
<div class="ai-gauge" aria-label="발주 정보 진행률">
    <div class="ai-gauge-track"><div class="ai-gauge-fill"></div></div>
    <span class="ai-gauge-count" aria-live="polite"></span>
    <span class="ai-gauge-usage"></span>
</div>
<!-- disabled 로 시작한다 — 사전 조건(결제방식)이 없는 회원이면 첫 _syncChatLock() 이
     "막 풀린 순간"으로 보고 입력창에 포커스를 준다. -->
<form class="ai-form disabled" autocomplete="off">
    <div class="ai-field">
        <textarea class="ai-input" rows="1" placeholder="예) 내일 10시 강남 스카이 25톤" required></textarea>
        <button type="submit" class="ai-send" aria-label="전송"><i class="fas fa-paper-plane" aria-hidden="true"></i></button>
    </div>
</form>
        `);
        // 컨테이너에 layout class 부여
        $('#' + this.id).addClass('ai-chat');
    }

    _bind() {
        const self = this;
        const $root = $('#' + this.id);

        // 모달을 닫았다 다시 열면 _bind() 가 또 돌아 같은 핸들러가 겹쳐 붙는다.
        // 그러면 「수정」 한 번에 _editSlot 이 두 번 돌아 안내문이 두 줄씩 쌓인다.
        $root.off('.aiChat');

        // 예시 클릭 → 입력란
        $root.on('click.aiChat', '.ai-hint .ex', function () {
            $root.find('.ai-input').val($(this).text()).focus();
        });

        // 전송
        $root.on('submit.aiChat', '.ai-form', function (e) {
            e.preventDefault();
            self._send();
        });

        // 잠금 상태(결제방식 미선택)에서 채팅을 건드리면 — 포커스·타이핑·붙여넣기 —
        // 왜 잠겼는지 토스트로 안내하고 막는다. (disabled 를 안 쓰는 이유는 _syncChatLock 주석)
        $root.on('mousedown.aiChat touchstart.aiChat keydown.aiChat paste.aiChat', '.ai-input', function (e) {
            if (self._isPreChatSatisfied()) return;
            e.preventDefault();
            e.stopImmediatePropagation(); // 아래 Enter=전송 keydown 핸들러로 이어지지 않게
            self._showLockToast();
            this.blur();
        });
        // 전송 버튼도 잠금 상태면 토스트만 (폼 submit 로 이어지지 않도록 preventDefault)
        $root.on('click.aiChat', '.ai-send', function (e) {
            if (self._isPreChatSatisfied()) return;
            e.preventDefault();
            e.stopImmediatePropagation();
            self._showLockToast();
        });

        // 입력창이 textarea 라 Enter 로 폼 submit 이 걸리지 않는다 — 직접 전송한다.
        // Shift+Enter 는 줄바꿈(기본 동작 유지). 한글 조합 중(isComposing)에는 전송하지 않는다.
        $root.on('keydown.aiChat', '.ai-input', function (e) {
            if (e.key !== 'Enter' || e.shiftKey || e.originalEvent?.isComposing) return;
            e.preventDefault();
            self._send();
        });

        // 붙여넣은 여러 줄이 잘려 보이지 않도록 내용에 맞춰 높이를 늘린다(CSS max-height 로 상한).
        $root.on('input.aiChat', '.ai-input', function () {
            this.style.height = 'auto';
            this.style.height = this.scrollHeight + 'px';
        });

        // 상단 바
        $root.on('click.aiChat', '.ai-freq',    () => self._openFreqSheet());
        $root.on('click.aiChat', '.ai-restart', () => self._restart());

        // 요약 카드 — 항목 수정 / 발주자=현장담당자 / CTA
        $root.on('click.aiChat', '.ai-sum-row[data-slot]', function () {
            self._editSlot($(this).data('slot'));
        });
        $root.on('click.aiChat', '.ai-sum-check', function () {
            self._toggleFieldContact();
        });
        // 행이 button 이 아니라 div[role] 이라 Enter·Space 를 직접 받아야 한다
        $root.on('keydown.aiChat', '.ai-sum-row', function (e) {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            const slot = $(this).data('slot');
            if (slot) self._editSlot(slot);
            else if ($(this).hasClass('ai-sum-check')) self._toggleFieldContact();
        });
        $root.on('click.aiChat', '.ai-sum-cta', function () {
            self._commit();
        });

        // 줄 안 입력칸 — 줄 전체를 누르면 편집이 취소되므로 여기서 버블링을 끊는다
        $root.on('click.aiChat keydown.aiChat', '.ai-sum-input', function (e) {
            e.stopPropagation();
            if (e.type === 'keydown' && e.key === 'Enter') {
                e.preventDefault();
                self._submitInlineEdit($(this).val());
            }
        });
        $root.on('click.aiChat', '.ai-sum-ok', function (e) {
            e.stopPropagation();
            self._submitInlineEdit($(this).closest('.ai-sum-row').find('.ai-sum-input').val());
        });
        $root.on('click.aiChat', '.ai-sum-undo', function (e) {
            e.stopPropagation();
            self._cancelEdit();
        });
    }

    // HTML 텍스트/속성 컨텍스트에 안전하게 넣기 위한 최소 이스케이프.
    // suggestion.label 등은 회원이 과거에 입력한 자유텍스트(car_option)를 포함할 수 있어 반드시 거친다.
    _escapeHtml(str) {
        return String(str ?? '').replace(/[&<>"']/g, (c) => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
        }[c]));
    }

    _focusInput() {
        setTimeout(() => $('#' + this.id).find('.ai-input').focus(), 50);
    }

    // ── 사전 단계: 회사별 차별화(삼성건) ──────────────────────────────────
    // 머큐리 보험건(접수번호·보험사 조회·계산서)은 여기서 받지 않는다 — 발주폼(Sosok/Mercury.js)이
    // 정본이고, 챗봇은 폼으로 넘긴 뒤 발주자가 거기서 접수번호를 넣고 [조회]한다.
    _prepareSosokRequirement() {
        const sosok = MEMBER['sosok'] || '';
        if (sosok.substring(0, 3) === '019') {
            this.sosokReq = { type: 'samsung', title: '수임번호' };
        } else {
            this.sosokReq = null;
        }
    }

    // ── 사전 단계: 결제방식 적용 대상 여부 ───────────────────────────────
    // 실제 발주폼(Render.js:updateHowtopayUI())의 조건을 그대로 반영: 업체발주자이거나
    // 장비불러 소속이 아니면 결제방식은 고정값이라 선택지 자체가 없다(stepSkip=['howtopay']).
    // 반대로 "수발주자 + 장비불러 소속"인 경우에만 결제방식을 실제로 고를 수 있다.
    _preparePayment() {
        this.needPayment = (MEMBER['mem_driver_type'] !== '업체발주자') && Util.isSosokJbbuller(MEMBER['sosok']);
    }

    // ── 자주 발주한 조합 조회 ────────────────────────────────────────────
    async _fetchPatterns() {
        try {
            const res = await fetch('/m/ajax/bal/ai/suggest', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            const data = await res.json();
            this.patterns = Array.isArray(data.suggestions) ? data.suggestions
                          : (data.suggestion ? [data.suggestion] : []);
            $('#' + this.id).find('.ai-topbar').css('display', this.patterns.length ? '' : 'none');
        } catch (err) {
            // 제안은 부가기능 — 실패해도 채팅 자체는 계속 가능해야 하므로 조용히 무시.
            // 상단 바는 「처음부터」만으로는 띄우지 않는다(자주발주가 있을 때만 노출).
        }
    }

    /**
     * 채팅 입력을 열어도 되는 상태인지.
     *
     * 결제방식만 잠근다 — 도크 버튼으로 고르는 값이라 입력창이 잠겨도 진행할 수 있다.
     *
     * 삼성 수임번호는 잠그면 안 된다. 실제 업무는 상담(불러톡) 원문을 통째로 붙여넣으면 서버가
     * 거기서 수임번호를 뽑는 흐름인데(BalAiController::extract), 입력창이 막히면 그 경로가 죽는다.
     * 대신 수임번호가 없으면 「발주 폼으로 이동」을 막는다(_requiredFields).
     */
    _isPreChatSatisfied() {
        if (this.needPayment && !this.currentFields.howtopay) return false;
        return true;
    }

    /**
     * 이 회원에게 필수인 필드 목록. 삼성 소속은 수임번호(bonsa_col)가 추가된다.
     * 서버에 남은 선택지를 물을 때(_refreshQuickReplies)와 되묻기(_renderFreeTextPrompt)가 본다.
     */
    _requiredFields() {
        const req = [...AiChatModal.REQUIRED];
        if (this.sosokReq?.type === 'samsung') req.push('bonsa_col');
        return req;
    }

    /**
     * 사전 필수값(결제방식) 충족 여부에 따라 채팅 입력을 잠그거나 푼다.
     *
     * 한 번 풀리면 다시 잠기지 않는 단방향이었던 탓에, 수임번호를 10자 이상 넣어 채팅을 연 뒤
     * 다시 지워도 잠기지 않아 필수값 없이 발주가 진행되는 버그가 있었다(2026-07-14 실사용 검증).
     * → 상태를 볼 때마다 양방향으로 동기화한다.
     *
     * 입력창을 disabled 로 막지 않는다 — 잠금 상태에서 사용자가 입력창을 건드리면 왜 잠겼는지
     * 토스트로 안내해야 하는데, disabled 는 클릭 이벤트조차 안 잡히기 때문. 대신 disabled 클래스로
     * 흐리게만 표시하고, 실제 타이핑·전송 차단은 _bind() 의 가드 핸들러 + _send() 가 맡는다.
     */
    _syncChatLock() {
        const $form = $('#' + this.id).find('.ai-form');
        const ok = this._isPreChatSatisfied();
        const wasLocked = $form.hasClass('disabled');

        $form.toggleClass('disabled', !ok);

        if (!ok || !wasLocked) return; // 막 풀린 순간에만 입력창으로 포커스
        this._focusInput();
    }

    /**
     * 잠금 상태에서 채팅을 건드렸을 때, 무엇을 먼저 해야 하는지 토스트로 안내한다.
     */
    _showLockToast() {
        $j.Ui.Toast.show('먼저 아래에서 결제방식을 선택해주세요.', { type: 'warning', closable: true });
    }

    /**
     * 예시 칩 + 오늘 사용량을 서버에서 받아 반영. 실패하면 기본 샘플 유지·배지 숨김.
     */
    async _loadExamples() {
        try {
            const res = await fetch('/m/ajax/bal/ai/examples', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: '{}',
            });
            const data = await res.json();

            const list = Array.isArray(data.examples) ? data.examples : [];
            if (list.length > 0) {
                const $ex = $('#' + this.id).find('.ai-hint .examples');
                $ex.empty();
                list.forEach((t) => $ex.append($('<span class="ex"></span>').text(t)));
            }
            this._renderUsage(data.usage);
        } catch (e) {
            // 조용히 실패
        }
    }

    /**
     * 사용량 표시 갱신 — {used, limit}. 게이지 오른쪽에 붙는다.
     * 80% 이상이면 warn(주황), 한도 도달이면 danger(빨강).
     */
    _renderUsage(usage) {
        if (!usage || typeof usage.limit !== 'number') return;
        const $u = $('#' + this.id).find('.ai-gauge-usage');
        const { used, limit } = usage;
        $u.removeClass('warn danger');
        if (used >= limit)            $u.addClass('danger');
        else if (used / limit >= 0.8) $u.addClass('warn');
        $u.text(`오늘 ${used}/${limit}회`);
    }

    _addMsg(text, cls) {
        const $log = $('#' + this.id).find('.ai-log');
        const $el = $('<div class="ai-msg"></div>').addClass(cls).text(text);
        $log.append($el);
        $log.scrollTop($log[0].scrollHeight);
        return $el;
    }

    // ─────────────────────────────────────────────────────────────────────
    // 진행 게이지
    // ─────────────────────────────────────────────────────────────────────
    _renderGauge() {
        const slots = this._slots();
        const n = slots.filter((s) => this._slotFilled(s)).length;
        const total = slots.length;
        const $root = $('#' + this.id);

        $root.find('.ai-gauge-fill').css('width', (total ? (n / total) * 100 : 0) + '%');
        $root.find('.ai-gauge-count')
            .text(n >= total ? `✓ ${n}/${total}` : `${n}/${total}`)
            .toggleClass('done', n >= total);
    }

    // ─────────────────────────────────────────────────────────────────────
    // 선택지 도크 (입력창 바로 위 고정)
    // ─────────────────────────────────────────────────────────────────────

    /**
     * 도크에 깔 스텝 목록. 서버 선택지(quickReplies)와 같은 모양으로 로컬 스텝을 앞에 붙인다.
     * 결제방식은 채팅을 잠그는 값이라 항상 맨 앞에 온다.
     */
    _dockSteps() {
        const steps = [];

        // 결제방식은 서버 /options 가 만들지 않는다 — 여기서 직접 만든다.
        // 값이 있어도 '수정' 으로 편집 중이면 다시 고를 수 있게 띄운다.
        if (this.needPayment && (!this.currentFields.howtopay || this._editingSlot === 'pay')) {
            // 발주자는 싸인(1) 제외 — 결제책임 BS(HowToPay.js)와 동일 정책.
            // 채팅으로 "싸인" 이라고 말하는 경로는 서버(BalAiController::extract)가 막는다.
            const options = Object.keys(G5['HOW_TO_PAY'] || {})
                .filter((k) => !(MEMBER['mem_driver_type'] == '발주자' && k == 1))
                .map((k) => ({ value: k, label: G5['HOW_TO_PAY'][k] }));
            steps.push({ field: 'howtopay', prompt: '결제방식을 선택해주세요', options, required: true });
        }

        return steps.concat(this._quickReplies || []);
    }

    _renderDock() {
        const self = this;
        const $dock = $('#' + this.id).find('.ai-dock');
        const steps = this._dockSteps();

        $dock.empty();
        if (steps.length === 0) { $dock.hide(); return; }

        steps.forEach((step) => {
            const $step = $('<div class="ai-dock-step"></div>');
            const $hint = $('<div class="ai-dock-hint"></div>').text(step.prompt);
            if (step.required) $hint.append($('<span class="req">*</span>'));
            $step.append($hint);

            const $opts = $('<div class="ai-dock-opts" role="group"></div>');
            step.options.forEach((opt) => {
                const $btn = $('<button type="button" class="ai-opt"></button>').text(opt.label);
                $btn.on('click', () => {
                    // 고른 항목의 스텝은 즉시 걷는다 — 서버(_refreshQuickReplies)가 답할 때까지
                    // 방금 고른 선택지가 그대로 남아 있으면 반응이 없는 것처럼 보인다.
                    self._quickReplies = (self._quickReplies || []).filter((q) => q.field !== step.field);
                    self._applyQuickReply(step.field, opt);
                    self._renderDock();
                });
                $opts.append($btn);
            });
            // 선택지만으로 못 고르는 값은 옆에 보조 수단을 둔다.
            // 날짜는 오늘·내일·모레 3개뿐이고(달력을 버튼으로 깔 수 없어서),
            // 작업소요도 4개뿐이라 그 밖의 값은 채팅으로만 가능했다.
            if (step.field === 'work_day') {
                const $cal = $('<button type="button" class="ai-opt ai-cal-btn"></button>').text('📅 달력');
                $cal.on('click', () => self._openCalendar());
                $opts.append($cal);
            }
            if (step.field === 'work_time') {
                const $tp = $('<button type="button" class="ai-opt ai-cal-btn"></button>').text('🕑 시간 선택');
                $tp.on('click', () => self._openTimePicker());
                $opts.append($tp);
            }
            $step.append($opts);
            $dock.append($step);
        });

        $dock.css('display', '');
    }

    /**
     * 발주 폼(WorkDay.js)과 같은 mobiscroll 달력을 대화 흐름 안에 깐다.
     *
     * 서버가 주는 날짜 선택지는 오늘·내일·모레 3개뿐이라(달력을 버튼으로 깔 수 없어서)
     * 그 밖의 날짜는 채팅으로만 말할 수 있었다. 채팅은 AI 호출을 쓰므로 하루 한도가 준다.
     */
    _openCalendar() {
        const self = this;
        const $log = $('#' + this.id).find('.ai-log');
        $log.find('.ai-cal').remove();

        const $wrap = $('<div class="ai-cal"><div class="ai-cal-inner"></div></div>');
        $log.append($wrap);

        const cur = this.currentFields.work_day ? dayjs(this.currentFields.work_day).toDate() : null;
        $wrap.find('.ai-cal-inner').mobiscroll().datepicker({
            controls: ['calendar'],
            display: 'inline',
            locale: mobiscroll.localeKo,
            defaultSelection: cur,
            buttons: [],
            renderCalendarHeader: $j.Common.Mobiscroll.HEADER_PLAIN,  // 칸 클릭으로 답한다 — 내장 '오늘'은 반영이 안 돼 뺀다
            onCellClick: function (args, inst) {
                if (!args.date) return;
                if (args.outer) { inst.navigate(args.date); return; }  // 다른 달 칸이면 그 달로 이동만
                inst.setVal(args.date, true);
                const d = dayjs(args.date);
                $wrap.remove();
                self._quickReplies = (self._quickReplies || []).filter((q) => q.field !== 'work_day');
                self._applyQuickReply('work_day', { value: d.format('YYYY-MM-DD'), label: d.format('M월 D일') });
                self._renderDock();
            },
        });
        $log.scrollTop($log[0].scrollHeight);
    }

    /**
     * 발주 폼(WorkTime.js)과 같은 시·분 선택기를 대화 흐름 안에 깐다.
     *
     * 서버가 주는 시간 선택지는 몇 개뿐이라 그 밖의 시각은 채팅으로만 말할 수 있었다.
     * 채팅은 AI 호출을 쓰므로 하루 한도가 준다.
     */
    _openTimePicker() {
        const self = this;
        const $log = $('#' + this.id).find('.ai-log');
        $log.find('.ai-tp').remove();

        const cur = {
            h: this.currentFields.work_start_h != null ? String(this.currentFields.work_start_h).padStart(2, '0') : null,
            m: this.currentFields.work_start_m != null ? String(this.currentFields.work_start_m).padStart(2, '0') : '00',
        };
        const cell = (v, on, suffix) =>
            `<button type="button" class="ai-tp-cell${on ? ' on' : ''}" data-v="${v}">${v}<small>${suffix}</small></button>`;

        const hours = Array.from({ length: 24 }, (_, i) => {
            const v = String(i).padStart(2, '0');
            return cell(v, v === cur.h, '시');
        }).join('');
        const mins = ['00', '10', '20', '30', '40', '50'].map((v) => cell(v, v === cur.m, '분')).join('');

        const $wrap = $(`
<div class="ai-tp">
    <div class="ai-tp-title">시</div><div class="ai-tp-grid hour">${hours}</div>
    <div class="ai-tp-title">분</div><div class="ai-tp-grid min">${mins}</div>
    <div class="ai-tp-foot">
        <span class="ai-tp-preview"></span>
        <button type="button" class="ai-tp-ok">적용</button>
        <button type="button" class="ai-tp-cancel">취소</button>
    </div>
</div>`);
        $log.append($wrap);

        // 시를 누르자마자 닫으면 분을 못 고친다 — 고르는 동안은 미리보기만 갱신하고
        // 「적용」에서 한 번에 반영한다 (발주 폼 WorkTime.js 도 고른 뒤 적용하는 흐름).
        const pick = { h: cur.h, m: cur.m };
        const preview = () => {
            $wrap.find('.ai-tp-preview').text(
                pick.h == null ? '시를 고르세요' : self._timeKo(Number(pick.h), Number(pick.m))
            );
            $wrap.find('.ai-tp-ok').prop('disabled', pick.h == null);
        };
        preview();

        const select = (sel, key) => function () {
            pick[key] = String($(this).data('v')).padStart(2, '0');
            $wrap.find(sel + ' .ai-tp-cell').removeClass('on');
            $(this).addClass('on');
            preview();
        };
        $wrap.on('click', '.ai-tp-grid.hour .ai-tp-cell', select('.ai-tp-grid.hour', 'h'));
        $wrap.on('click', '.ai-tp-grid.min  .ai-tp-cell', select('.ai-tp-grid.min',  'm'));

        $wrap.on('click', '.ai-tp-cancel', () => $wrap.remove());
        $wrap.on('click', '.ai-tp-ok', () => {
            if (pick.h == null) return;
            $wrap.remove();
            self._quickReplies = (self._quickReplies || []).filter((q) => q.field !== 'work_time');
            self._applyQuickReply('work_time', {
                value: `${pick.h}:${pick.m}`,
                label: self._timeKo(Number(pick.h), Number(pick.m)),
            });
            self._renderDock();
        });

        $log.scrollTop($log[0].scrollHeight);
    }

    _applyQuickReply(field, opt) {
        // car_workhour 는 "값|단위" 포맷 — 값과 단위를 각각 다른 필드에 넣는다
        if (field === 'car_workhour') {
            const [hour, unit] = opt.value.split('|');
            this.currentFields.car_workhour = hour;
            this.currentFields.car_workdaytype = unit;
        } else if (field === 'car_range') {
            this.currentFields.car_range = opt.value;
            this.currentDisplay.car_range = opt.label;
        } else if (field === 'car_type') {
            // 차종이 바뀌면 이전 스펙/옵션은 더 이상 유효하지 않다(_send() 의 동일 처리와 같은 이유)
            if (opt.value !== this.currentFields.car_type) {
                delete this.currentFields.car_range;
                delete this.currentFields.car_option;
                delete this.currentDisplay.car_range;
            }
            this.currentFields.car_type = opt.value;
        } else if (field === 'work_day') {
            this.currentFields.work_day = opt.value;   // "2026-07-15"
            this.currentDisplay.work_day = opt.label;  // "내일 (7/16)" — 요약 카드가 날짜를 사람 말로 보여주도록
            this._syncWorkTime();
        } else if (field === 'work_time') {
            const [h, m] = opt.value.split(':');       // "10:00"
            this.currentFields.work_start_h = h;
            this.currentFields.work_start_m = m;
            this._syncWorkTime();
        } else if (field === 'howtopay') {
            this.currentFields.howtopay = parseInt(opt.value, 10);
            this.currentDisplay.howtopay = opt.label;
            this._syncChatLock();
        } else {
            this.currentFields[field] = opt.value;
        }
        this._addMsg(`선택: ${opt.label}`, 'me');
        this.chatHistory.push({ role: 'user', text: opt.label });
        this._finishEdit(field);
        this._afterFieldsChanged();
        // 값이 채워지면 남은 선택지가 달라진다 (예: 장비를 고르면 그 장비의 스펙 목록이 생김)
        this._refreshQuickReplies();
    }

    /**
     * 서버에 남은 선택지를 다시 물어본다 (AI 호출 없음 → 일일 한도 소모 없음).
     *
     * 최근패턴 칩·선택지 버튼으로 값을 채우면 서버 왕복이 없어 선택지가 그대로 남는다.
     * 예: 칩으로 장비·스펙을 채웠는데 작업량·날짜·시간 버튼이 안 뜨는 문제.
     */
    async _refreshQuickReplies(omit = null) {
        // 편집 중이면 그 칸을 뺀 사본을 보낸다 — 서버는 '비어 있는 칸'의 선택지만 돌려주므로,
        // 값을 그대로 둔 채 목록을 받으려면 이 방법뿐이다. 화면 값(currentFields)은 손대지 않는다.
        const payload = { ...this.currentFields };
        if (omit) omit.forEach((f) => { delete payload[f]; });
        try {
            const res = await fetch('/m/ajax/bal/ai/options', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ fields: payload }),
            });
            const data = await res.json();
            if (data.ok) {
                this._quickReplies = data.quickReplies || [];
                this._renderDock();
                this._renderFreeTextPrompt(data.missing || [], this._quickReplies);
                this._afterFieldsChanged();
            }
        } catch (err) {
            // 선택지는 부가기능 — 실패해도 채팅으로 계속 진행할 수 있어야 하므로 조용히 무시
        }
    }

    /**
     * 선택지로 만들 수 없어 채팅으로 받아야 하는 값(주소 등)이 남았을 때 무엇을 입력하면 되는지 알려준다.
     *
     * 칩·선택지 버튼으로만 진행하면 AI 가 말을 걸 기회가 없어, 마지막에 주소만 남았는데
     * 아무도 안 물어보는 상태가 된다. AI 호출 없이 화면에서만 띄우므로 일일 한도를 소모하지 않는다.
     */
    _renderFreeTextPrompt(missing, quickReplies) {
        const $log = $('#' + this.id).find('.ai-log');
        $log.find('.ai-ask').remove(); // 항상 대화 맨 아래에 오도록 매번 다시 그린다

        // 아직 아무것도 안 채운 상태(모달 첫 진입)면 안내하지 않는다 — 상단 안내문구로 충분
        if (!AiChatModal.REQUIRED.some((k) => this.currentFields[k])) return;

        const asks = [];

        // 삼성 수임번호는 채팅을 잠그지 않는다 — 상담 원문을 붙여넣으면 서버가 거기서 뽑아준다.
        // 그 경로로도 안 나왔을 때만 되묻는다.
        if (this.sosokReq?.type === 'samsung' && !this.currentFields.bonsa_col) {
            asks.push('수임번호를 찾지 못했어요. 상담 내용을 그대로 붙여넣거나, 수임번호를 입력해주세요.');
        }

        // 선택지로 못 만드는 값(주소 등)은 채팅으로 받아야 하니 명시적으로 되묻는다
        const covered = new Set((quickReplies || []).map((qr) => qr.field));
        const rest = (missing || []).filter((k) => !covered.has(k) && AiChatModal.LABEL[k]);
        if (rest.length > 0) {
            const HINT = { contract_address: '현장 주소를 알려주세요. (예: 강남역, 서울 강남구 테헤란로 123)' };
            asks.push((rest.length === 1 && HINT[rest[0]])
                ? HINT[rest[0]]
                : rest.map((k) => AiChatModal.LABEL[k]).join('·') + '을(를) 알려주세요.');
        }

        if (asks.length === 0) return;

        $log.append($('<div class="ai-msg bot ai-ask"></div>').text(asks.join('\n')));

        // 주소는 좌표까지 있어야 매칭이 되고, 말로 하면 동명이지(同名異地)로 엉뚱한 곳이 잡혀도
        // 확인할 방법이 없다. 발주 폼과 같은 지도를 띄워 핀으로 고를 수 있게 한다.
        if (rest.includes('contract_address')) {
            const self = this;
            const $btn = $('<button type="button" class="ai-opt ai-map-btn"></button>').text('🗺 지도에서 선택');
            $btn.on('click', function () { self._openMapPicker(); });
            $log.append($('<div class="ai-ask"></div>').append($btn));
        }

        $log.scrollTop($log[0].scrollHeight);
    }

    // ─────────────────────────────────────────────────────────────────────
    // 회사별 필수값을 대화로 받기
    // ─────────────────────────────────────────────────────────────────────


    /**
     * 요약 카드에서 메모를 누른 뒤 들어온 입력을 그대로 메모로 받는다.
     * @returns {boolean} 이번 입력을 메모로 소비했는지
     */
    _consumeMemo(text) {
        if (!this._awaitingMemo) return false;
        this._awaitingMemo = false;
        this.currentFields.contract_message = text;
        this._addMsg('메모를 반영했어요.', 'bot');
        this._finishEdit();
        this._afterFieldsChanged();
        return true;
    }

    // ─────────────────────────────────────────────────────────────────────
    // 「자주 발주」 시트
    // ─────────────────────────────────────────────────────────────────────
    _openFreqSheet() {
        if (this.patterns.length === 0) {
            $j.Ui.Toast.show('아직 자주 발주하신 내역이 없어요.', { type: 'warning', closable: true });
            return;
        }

        const rows = this.patterns.map((p, i) => `
<button type="button" class="ai-freq-item" data-idx="${i}">
    <span class="txt"><b>${this._escapeHtml(p.label)}</b><small>최근 ${p.cnt || 0}회 발주</small></span>
    <span class="go" aria-hidden="true"><i class="fas fa-chevron-right"></i></span>
</button>`).join('');

        // 새 시트는 $j.Ui.Modal 을 쓴다 (.ai/frontend.md) — BS 안에서 BS 를 열면 안 되고,
        // Modal 은 z 슬롯이 BS 보다 위라 이 채팅 위에 그대로 스택된다.
        this._freqSheet = $j.Ui.Modal.open({
            title: '자주 하신 작업',
            mode: 'sheet',
            panelClass: 'ai-freq-sheet',
            html: `<p class="ai-freq-sub">한 번에 장비·스펙을 채우고 시작해요.</p><div class="ai-freq-list">${rows}</div>`,
        });

        this._freqSheet.el.on('click', '.ai-freq-item', (e) => {
            const idx = parseInt($(e.currentTarget).data('idx'), 10);
            this._applyPattern(this.patterns[idx]);
        });
    }

    /** 자주 발주 항목 선택 → 장비·스펙·옵션을 한 번에 채운다 (AI 왕복 없음). */
    _applyPattern(p) {
        if (!p) return;
        this._freqSheet?.close?.();
        this._freqSheet = null;

        this.currentFields.car_type   = p.car_type;
        this.currentFields.car_range  = p.car_range;
        this.currentDisplay.car_range = p.label;
        if (p.car_option) this.currentFields.car_option = p.car_option;

        this._addMsg(`${p.label} · 이걸로 시작`, 'me');
        // chatHistory 에도 반영 — 선택지 버튼(_applyQuickReply)과 동일하게, 다음 /extract 요청에
        // 이 선택이 대화 맥락으로 전달되도록 한다("저번처럼" 이후 이어지는 발화 해석에 영향).
        this.chatHistory.push({ role: 'user', text: `최근 패턴 적용: ${p.label}` });
        // 남은 항목 안내는 _renderFreeTextPrompt() 가 실제 상태를 보고 띄운다.
        // 여기서 문구를 고정해두면 실제로 빠진 것(작업량 등)과 어긋난다.
        this._addMsg('반영했어요.', 'bot');

        this._afterFieldsChanged();
        this._refreshQuickReplies(); // 칩으로 장비·스펙이 채워졌으니 남은 항목의 선택지를 받아온다
    }

    /** 처음부터 다시 — 누적된 필드·대화를 버리고 첫 화면으로 되돌린다. */
    _restart() {
        this.chatHistory = [];
        this.currentFields = {};
        this.currentDisplay = {};
        this._quickReplies = [];
        this._awaitingMap = false;
        this._awaitingMemo = false;
        this._aiFieldCall = {};
        this._lowConfTries = 0;
        this._editingSlot = null;
        this._editBackup = null;

        const $root = $('#' + this.id);
        $root.find('.ai-log .ai-msg, .ai-log .ai-ask, .ai-log .ai-summary-card').remove();
        $root.find('.ai-hint').show();
        $root.find('.ai-input').val('').css('height', 'auto');

        this._syncChatLock();
        this._renderGauge();
        this._renderDock();
        $j.Ui.Toast.show('처음부터 다시 시작해요.', { closable: true });
    }

    // ─────────────────────────────────────────────────────────────────────
    // 전송
    // ─────────────────────────────────────────────────────────────────────
    async _send() {
        // 사전 필수값(결제방식)이 안 채워졌으면 전송 대신 안내 — 엔터·폼 submit 경유도 여기서 막힌다.
        if (!this._isPreChatSatisfied()) { this._showLockToast(); return; }

        const $root = $('#' + this.id);
        const $input = $root.find('.ai-input');
        const text = ($input.val() || '').trim();
        if (!text) return;

        this._addMsg(text, 'me');
        $input.val('');
        $input[0].style.height = 'auto'; // 여러 줄 붙여넣기로 늘어난 높이를 한 줄로 되돌린다
        $root.find('.ai-hint').hide();

        // 메모를 기다리는 중이면 AI 를 부르지 않는다 (일일 한도 소모 없음).
        // 메모는 문장에서 뽑을 값이 아니라 사용자가 적은 그대로 들어가야 하는 값이다.
        if (this._consumeMemo(text)) return;

        $root.find('.ai-send').prop('disabled', true);
        const $typing = this._addMsg('분석 중…', 'bot typing');

        try {
            const res = await fetch('/m/ajax/bal/ai/extract', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                // 최근 패턴 라벨은 보내지 않는다 — 서버(BalAiController::extract)가 회원 오더에서
                // 직접 조회해 AiOrderExtractor 에 넘긴다. 클라이언트가 보내도 무시된다.
                // known — 지금까지 누적한 필드. 서버가 '무엇이 남았나'를 이걸 합쳐서 판단한다.
                // 안 보내면 이번 턴에 AI 가 다시 안 말한 항목을 미입력으로 보고 또 물어본다.
                body: JSON.stringify({ message: text, history: this.chatHistory, known: this.currentFields }),
            });
            const data = await res.json();
            $typing.remove();

            if (data.ok) {
                this._addMsg(data.assistantReply || '(응답 없음)', 'bot');
                this.chatHistory.push({ role: 'user',  text });
                this.chatHistory.push({ role: 'model', text: data.assistantReply || '' });
                // 대화 중 장비종류가 바뀌면 이전 스펙/옵션은 더 이상 유효하지 않다. 이번 턴에 새 스펙이
                // 같이 오면 바로 아래 병합에서 덮어써지지만, 안 왔으면(모델이 스펙을 못 뽑았으면) 무효한
                // 값이 그대로 남아 새 차종과 짝이 안 맞는 car_range 가 커밋될 수 있어 먼저 지운다.
                // (발주폼 onModalApply() 가 차종 변경 시 car_range/car_option 을 지우는 것과 동일한 이유)
                if (data.fields?.car_type && data.fields.car_type !== this.currentFields.car_type) {
                    delete this.currentFields.car_range;
                    delete this.currentFields.car_option;
                    delete this.currentDisplay.car_range;
                }
                // 다음 턴이 일부 필드만 보내도 직전 누적이 유지되도록 merge.
                // postProcess 가 빈 값을 fields 에 안 넣으므로 새 값만 덮어쓰는 효과.
                // 이번 턴에 AI 가 실제로 값을 준 필드만 계측 행번호에 묶어둔다.
                // 사용자가 나중에 그 필드를 고치면 이 행이 '고쳐졌다'로 집계된다.
                if (data.aiCallNum) {
                    Object.keys(data.fields || {}).forEach((k) => { this._aiFieldCall[k] = data.aiCallNum; });
                }
                this.currentFields  = { ...this.currentFields,  ...(data.fields  || {}) };
                this.currentDisplay = { ...this.currentDisplay, ...(data.display || {}) };
                // 주소처럼 채팅으로 고치는 항목은 여기서 값이 들어온다 — 편집 상태를 푼다
                this._finishEdit();
                // AI 가 결제방식 등을 뽑아 왔으면 잠금·도크에 반영한다.
                this._syncChatLock();
                this._quickReplies = data.quickReplies || [];
                this._renderDock();
                this._afterFieldsChanged();
                this._applyNextAction(data.nextAction);
            } else {
                this._addMsg(data.error || data.assistantReply || '오류가 발생했습니다.', 'bot err');
            }
            // 사용량 갱신 (정상·에러·429 모두 서버에서 usage 동봉)
            this._renderUsage(data.usage);
        } catch (err) {
            $typing.remove();
            this._addMsg('네트워크 오류: ' + err.message, 'bot err');
        } finally {
            $root.find('.ai-send').prop('disabled', false);
            $input.focus();
        }
    }

    /**
     * 확신도에 따른 다음 행동 (기획 정본: jbbuller_doc `20260720_ai_order_map.md` 7장).
     *
     *   proceed      ≥0.8  그대로 진행 — 화면은 아무것도 더 하지 않는다
     *   button_fill  ≥0.5  빠진 값만 선택지로 — 도크가 이미 그 역할이라 추가 동작 없음
     *   followup     ≥0.3  되물어 재시도. 기획대로 2회까지, 넘으면 fallback 으로 떨어뜨린다
     *   fallback     <0.3  파악 실패 — 다시 시작하도록 안내
     *
     * nextAction 이 null 이면(확신도를 안 주는 프롬프트 버전) 분기하지 않는다.
     *
     * 되묻는 문구는 화면이 만든다. 기획의 04단계(LLM 이 질문을 생성)는 아직 미구현이라,
     * 여기서는 AI 를 다시 부르지 않는다 — 일일 한도를 쓰지 않는다.
     */
    _applyNextAction(nextAction) {
        if (!nextAction) return;

        if (nextAction === 'followup') {
            this._lowConfTries += 1;
            if (this._lowConfTries <= 2) {
                this._addMsg('말씀하신 내용만으로는 어떤 장비인지 확실하지 않아요. 어떤 작업인지 조금만 더 알려주세요.', 'bot');
                return;
            }
            nextAction = 'fallback'; // 2회 넘게 못 맞히면 더 되묻지 않는다
        }

        if (nextAction === 'fallback') {
            this._lowConfTries = 0;
            this._addMsg('죄송해요, 어떤 장비가 필요하신지 파악하지 못했어요.\n위 「처음부터」를 눌러 다시 시작하시거나, 장비 종류를 직접 알려주세요.', 'bot');
            return;
        }

        this._lowConfTries = 0; // proceed · button_fill 이면 되묻기 횟수를 되돌린다
    }

    /** 필드가 바뀔 때마다 게이지·요약 카드를 다시 맞춘다. */
    _afterFieldsChanged() {
        this._renderGauge();
        this._renderSummaryCard();
    }

    // ─────────────────────────────────────────────────────────────────────
    // 완료 요약 카드 — 대화 흐름 안에 붙는다
    // ─────────────────────────────────────────────────────────────────────
    _renderSummaryCard() {
        const $log = $('#' + this.id).find('.ai-log');
        $log.find('.ai-summary-card').remove(); // 재완료 시 중복 방지

        const slots  = this._slots();
        const editing = this._editingSlot;
        const editFields = editing ? this._editFieldsOf(editing) : [];
        const unfilled = slots.filter((s) => !this._slotFilled(s));

        // 평소엔 모든 칸이 차야 카드가 뜬다. 편집 중일 때만, 고치는 중인 그 칸 하나가 비어도
        // 카드를 유지한다 — 장비를 바꾸면 스펙이 딸려 비는데 그때 카드가 사라지면
        // 무엇을 고치는 중인지 화면에서 없어지기 때문이다.
        if (unfilled.length > 1) return;
        if (unfilled.length === 1 && unfilled[0].key !== this._slotKeyOf(editing)) return;
        // 삼성 수임번호는 슬롯이 아니라 필수 필드로만 잡혀 있다 — 없으면 커밋이 막히므로 카드도 아직
        if (this._requiredFields().some((k) => !this.currentFields[k] && !editFields.includes(k))) return;

        // 채팅으로 받던 값(메모·수임번호)은 이 줄에서 바로 칠 수 있게 한다.
        // 채팅창 입력 경로(_awaitingMemo)는 그대로 살아 있어 둘 다 쓸 수 있다.
        const inlineRow = (key, label, placeholder, current) => `
<div class="ai-sum-row editing inline" data-slot-edit="${key}">
    <span class="k">${this._escapeHtml(label)}</span>
    <span class="val"><input type="text" class="ai-sum-input" value="${this._escapeHtml(current || '')}"
        placeholder="${this._escapeHtml(placeholder)}" enterkeyhint="done"></span>
    <span class="ai-sum-ok"    role="button" tabindex="0">확인</span>
    <span class="ai-sum-undo"  role="button" tabindex="0" aria-label="수정 그만두기">취소</span>
</div>`;

        const inlineOf = (key) => {
            if (key === 'memo')    return inlineRow('memo', '메모', '특이사항을 입력하세요', this.currentFields.contract_message);
            if (key === 'suim')    return inlineRow('suim', '수임번호', '수임번호', this.currentFields.bonsa_col);
            return null;
        };

        const lineRow = (key, label, text) => {
            const on = key === editing;
            if (on) {
                const inline = inlineOf(key);
                if (inline) return inline;
            }
            const hint = key === 'addr' ? '지도에서 선택해 주세요' : '아래에서 선택해 주세요';
            const val  = text !== '' && text != null ? this._escapeHtml(text) : `<span class="muted">${hint}</span>`;
            return `
<div class="ai-sum-row${on ? ' editing' : ''}" role="button" tabindex="0" data-slot="${key}" aria-label="${this._escapeHtml(label)} ${on ? '수정 그만두기' : '수정하기'}">
    <span class="k">${this._escapeHtml(label)}</span>
    <span class="val">${val}</span>
    <span class="edit${on ? ' on' : ''}" aria-hidden="true">${on ? '선택 중' : '수정'}</span>
</div>`;
        };

        // 장비는 기종·스펙을 따로 고칠 수 있어야 한다 — 게이지는 한 칸으로 세되 줄만 나눈다
        const shown = (f) => (this.currentDisplay[f] != null && this.currentDisplay[f] !== '')
            ? this.currentDisplay[f] : this.currentFields[f];
        const row = (slot) => slot.key === 'gear'
            ? lineRow('gear_type',  '기종', shown('car_type'))
              + lineRow('gear_range', '스펙', shown('car_range'))
            : lineRow(slot.key, slot.label, this._slotText(slot));

        const memo = this.currentFields.contract_message;
        const memoRow = editing === 'memo'
            ? inlineOf('memo')
            : memo
            ? `<div class="ai-sum-row" role="button" tabindex="0" data-slot="memo" aria-label="메모 수정하기">
                   <span class="k">메모</span><span class="val">${this._escapeHtml(memo)}<em>선택</em></span>
                   <span class="edit" aria-hidden="true">수정</span>
               </div>`
            : `<div class="ai-sum-row add" role="button" tabindex="0" data-slot="memo" aria-label="메모 입력하기 (선택)">
                   <span class="k">메모</span><span class="val muted">특이사항이 있으면 입력하세요<em>선택</em></span>
                   <span class="edit add" aria-hidden="true">＋ 입력</span>
               </div>`;

        // 긴급은 사용자가 고르는 값이 아니라 도착시각에서 나온 결과라 읽기 전용 줄로 둔다.
        const quickRow = this._isQuickTime()
            ? `<div class="ai-sum-row quick"><span class="k">긴급</span>
                   <span class="val">곧 도착이라 <b>긴급 발주</b>로 접수됩니다</span></div>`
            : '';

        const checked = !!this.currentFields.contract_fieldnumber;
        const checkRow = `
<div class="ai-sum-row ai-sum-check${checked ? ' on' : ''}" role="checkbox" tabindex="0" aria-checked="${checked}">
    <span class="val"><b>발주자와 현장 담당자가 같으면 체크</b></span>
    <span class="checkbox" aria-hidden="true">${checked ? '✓' : ''}</span>
</div>`;

        $log.append(`
<div class="ai-summary-card">
    <div class="ai-sum-title"><span class="tick" aria-hidden="true">✓</span> 발주 내용 최종 확인</div>
    ${slots.map(row).join('')}
    ${quickRow}
    ${memoRow}
    ${checkRow}
    <div class="ai-sum-cta-wrap">
        <button type="button" class="ai-sum-cta"${editing ? ' disabled' : ''}>발주하기</button>
        <p class="ai-sum-note">${editing ? '수정을 마치면 발주할 수 있어요. 「선택 중」을 다시 누르면 되돌립니다.' : '다음 화면에서 금액을 확인하고 최종 발주합니다.'}</p>
    </div>
</div>`);

        // 카드 제목이 보이도록 — 카드가 길어 맨 아래로 내리면 무엇을 확인하는 화면인지 안 보인다
        if (!editing) {
            const card = $log.find('.ai-summary-card').get(0);
            $log.scrollTop(card ? Math.max(0, card.offsetTop - 8) : $log[0].scrollHeight);
        }
    }

    /**
     * 넘긴 필드 중 AI 가 채워준 것이 있으면, 그 값을 뽑은 호출을 '사용자가 고침'으로 표시한다.
     *
     * 이 표시가 프롬프트 버전별 정확도 지표가 된다 (app/Services/Ai/README.md 「계측」 —
     * user_modified 를 아무도 기록하지 않던 자리). AI 가 준 적 없는 필드(선택지 버튼으로만
     * 채운 값 등)를 고친 것은 AI 오답이 아니므로 보고하지 않는다.
     *
     * 지표용이라 결과를 기다리지 않고, 실패해도 무시한다.
     */
    _reportModified(fields) {
        const nums = [...new Set(
            (fields || []).map((f) => this._aiFieldCall[f]).filter(Boolean)
        )];
        if (nums.length === 0) return;

        // 한 번 보고한 호출은 다시 보내지 않는다 (같은 행을 여러 번 세울 이유가 없다)
        Object.keys(this._aiFieldCall).forEach((f) => {
            if (nums.includes(this._aiFieldCall[f])) delete this._aiFieldCall[f];
        });

        nums.forEach((num) => {
            fetch('/m/ajax/bal/ai/modified', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ num }),
            }).catch(() => {});
        });
    }

    /** 요약 카드에서 항목을 누르면 그 슬롯을 비우고 다시 묻는다. */
    /** 받침 유무로 을/를·이/가 를 고른다 — "주소을(를)" 처럼 어색하게 나오지 않도록. */
    _josa(word, withBatchim, withoutBatchim) {
        const last = (word || '').charCodeAt((word || '').length - 1);
        const hasBatchim = last >= 0xac00 && last <= 0xd7a3 && (last - 0xac00) % 28 !== 0;
        return word + (hasBatchim ? withBatchim : withoutBatchim);
    }

    /**
     * 편집 키 → 슬롯 key.
     *
     * 게이지는 「장비」를 한 칸으로 세지만(사람이 그렇게 인식한다), 카드에서는 기종과 스펙을
     * 따로 고칠 수 있어야 한다 — 스펙만 바꾸려는데 기종부터 다시 고르게 하면
     * 결국 발주 폼으로 나가서 고치게 된다(유실장님 제보 2026-08-26).
     */
    _slotKeyOf(key) {
        return (key === 'gear_type' || key === 'gear_range') ? 'gear' : key;
    }

    _labelOf(key) {
        if (key === 'gear_type')  return '기종';
        if (key === 'gear_range') return '스펙';
        if (key === 'memo')       return '메모';
        const slot = this._slots().find((s) => s.key === key);
        return slot ? slot.label : '';
    }

    /** 편집 키 → 실제로 그 줄이 쓰는 필드 이름들. */
    _editFieldsOf(key) {
        if (key === 'pay')        return ['howtopay'];
        if (key === 'memo')       return ['contract_message'];
        if (key === 'gear_type')  return ['car_type'];
        if (key === 'gear_range') return ['car_range'];
        const slot = this._slots().find((s) => s.key === key);
        return slot && slot.fields ? slot.fields.slice() : [];
    }

    /** 그 줄이 다시 채워졌는지. */
    _editFilled(key) {
        if (key === 'gear_type')  return !!this.currentFields.car_type;
        if (key === 'gear_range') return !!this.currentFields.car_range;
        if (key === 'memo')       return true;   // 메모는 비워두는 것도 선택
        const slot = this._slots().find((s) => s.key === key);
        return slot ? this._slotFilled(slot) : true;
    }

    /** 선택지로 만들 수 없어 채팅으로 받아야 하는 슬롯. */
    _isTypedSlot(key) {
        return key === 'addr' || key === 'memo' || key === 'suim';
    }

    /**
     * 남은 선택지를 받으려면 서버에 "그 칸이 비었다"고 알려야 한다.
     * 딸린 필드(작업소요의 단위, 시각의 시/분 등)까지 같이 빼야 반쯤 남은 값이 안 섞인다.
     */
    _editOmitFields(key) {
        const list = this._editFieldsOf(key);
        if (key === 'dur')  list.push('car_workdaytype');
        if (key === 'time') list.push('work_start_h', 'work_start_m');
        // 기종을 바꾸면 이전 스펙·옵션은 더 이상 유효하지 않다.
        // 스펙만 고칠 때는 기종을 남겨야 서버가 '그 기종의 스펙 목록'을 준다.
        if (key === 'gear' || key === 'gear_type') list.push('car_range', 'car_option');
        if (key === 'gear_range')                  list.push('car_option');
        return list;
    }

    /**
     * 요약 카드의 「수정」.
     *
     * 예전에는 카드를 지우고 값을 비웠다. 그러면 카드가 그려질 조건(모든 슬롯 채움)을 잃어
     * 무엇을 고치는 중인지 화면에서 통째로 사라지고, 고르다 그만둬도 되돌릴 값이 없었다.
     * 지금은 값을 그대로 둔 채 '편집 중' 만 표시하고, 서버에 물을 때만 그 칸을 뺀 사본을 보낸다.
     */
    _editSlot(key) {
        if (this._editingSlot === key) { this._cancelEdit(); return; }  // 같은 줄 다시 누르면 취소
        if (this._editingSlot) this._restoreEditBackup();               // 다른 줄로 갈아타면 먼저 되돌린다

        this._reportModified(this._editFieldsOf(key));
        this._editBackup  = { fields: { ...this.currentFields }, display: { ...this.currentDisplay } };
        this._editingSlot = key;

        if (key === 'memo') {
            this._awaitingMemo = true;
            this._addMsg('메모(특이사항)를 카드에서 바로 치거나, 여기 채팅창에 적어주세요. (선택)', 'bot');
            this._afterFieldsChanged();
            this._focusInlineInput();
            return;
        }

        // 주소는 좌표까지 있어야 매칭이 된다 — 말로 받지 말고 발주 폼과 같은 지도를 바로 띄운다.
        // AI 호출도 쓰지 않으므로 하루 한도를 아낀다.
        if (key === 'addr') {
            this._afterFieldsChanged();
            this._openMapPicker();
            return;
        }

        if (this._isTypedSlot(key)) {
            this._addMsg(`${this._josa(this._labelOf(key), '을', '를')} 카드에서 바로 치거나, 여기 채팅창에 적어주세요.`, 'bot');
            this._afterFieldsChanged();
            this._focusInlineInput();
            return;
        }

        this._afterFieldsChanged();
        this._refreshQuickReplies(this._editOmitFields(key));
    }

    /** 줄 안 입력칸에 커서를 둔다 — 카드를 다시 그린 뒤라야 요소가 있다. */
    _focusInlineInput() {
        const $inp = $('#' + this.id).find('.ai-sum-input').last();
        if ($inp.length) $inp.trigger('focus');
    }

    /**
     * 요약 카드 줄 안에서 친 값을 반영한다.
     * 채팅창으로 받는 경로(_consumeMemo)와 같은 자리에 넣어 결과가 갈라지지 않게 한다.
     */
    async _submitInlineEdit(raw) {
        const key  = this._editingSlot;
        const text = String(raw == null ? '' : raw).trim();
        if (!key) return;

        if (key === 'memo') {
            if (text) this.currentFields.contract_message = text;
            else      delete this.currentFields.contract_message;   // 비우면 메모를 지운다
            this._awaitingMemo = false;
            this._finishEdit();
            this._afterFieldsChanged();
            return;
        }
        if (key === 'suim') {
            if (!text) return;
            this.currentFields.bonsa_col = text;
            this._finishEdit('bonsa_col');
            this._afterFieldsChanged();
            return;
        }
    }

    /** 편집 시작 전 값으로 되돌린다 (카드 갱신은 호출한 쪽에서). */
    _restoreEditBackup() {
        if (this._editBackup) {
            this.currentFields  = { ...this._editBackup.fields };
            this.currentDisplay = { ...this._editBackup.display };
        }
        this._editingSlot   = null;
        this._editBackup    = null;
        this._awaitingMemo  = false;
    }

    /** 「선택 중」을 다시 눌러 편집을 그만둔다. 값은 편집 전으로 돌아간다. */
    _cancelEdit() {
        this._restoreEditBackup();
        this._syncChatLock();
        this._afterFieldsChanged();
        this._refreshQuickReplies();
    }

    /** 편집 중이던 줄이 다시 채워졌으면 편집 상태를 푼다. */
    _finishEdit(changedField) {
        if (!this._editingSlot) return;
        if (changedField && !this._editFieldsOf(this._editingSlot).includes(changedField)) return;
        if (!this._editFilled(this._editingSlot)) return;

        // 기종을 바꾸면 이전 스펙이 무효가 된다 — 카드를 닫지 말고 스펙 고르기로 이어간다.
        // (되돌림 값은 그대로 둬서 「선택 중」을 누르면 기종·스펙이 함께 원래대로 돌아간다)
        if (this._editingSlot === 'gear_type' && !this.currentFields.car_range) {
            this._editingSlot = 'gear_range';
            return;
        }

        this._editingSlot = null;
        this._editBackup  = null;
    }

    /**
     * 「발주자와 현장 담당자가 같으면 체크」
     *
     * 이 토글의 본질은 "현장담당자 = 나" 이므로 연락처가 핵심. 연락처가 없으면 빈 값으로
     * "성공한 것처럼" 채우지 않고 막는다(검증 없이 커밋·제출까지 흘러가는 필드라서).
     * 거래처(고객사)는 발주자 본인과 별개 개념이라 건드리지 않는다 — 사용자가 직접 입력.
     */
    _toggleFieldContact() {
        if (this.currentFields.contract_fieldnumber) {
            delete this.currentFields.contract_fieldnumber;
        } else {
            if (!MEMBER['mem_phone']) {
                $j.Ui.Toast.show('회원 연락처가 없어 자동으로 채울 수 없어요.', { type: 'warning', closable: true });
                return;
            }
            this.currentFields.contract_fieldnumber = MEMBER['mem_phone'];
        }
        this._renderSummaryCard();
    }

    /**
     * 발주 폼과 같은 카카오 지도(전체화면 iframe)를 띄운다.
     *
     * 발주 폼의 Render.openMap() 은 지도를 열기 전에 바텀시트를 닫지만, 여기서 닫으면 채팅 내용이
     * 통째로 사라진다. 지도는 body 최상단 전체화면 레이어(z-index-fullscreen)라 바텀시트를 닫지
     * 않아도 채팅 위를 그대로 덮는다.
     */
    _openMapPicker() {
        this._awaitingMap = true;
        $j.F.KakaoMap.open(
            this.currentFields.mem_basepoint || '',
            '1',
            this.currentFields.contract_address || ''
        );
    }

    /**
     * 지도에서 「적용」을 누르면 baljoo.blade.php 의 전역 callbackAddress() 가 이 메서드를 먼저 호출한다.
     * 챗봇이 연 지도면 결과를 챗봇이 가져가고(true), 아니면 그대로 발주 폼으로 흘려보낸다(false).
     *
     * 지도는 좌표 없이는 적용 자체가 안 되므로(KakaoMap.apply() 의 위경도 검사), 이 경로로 들어온
     * 주소는 항상 mem_basepoint 가 같이 온다.
     *
     * @returns {boolean} 이 챗봇이 결과를 소비했는지
     */
    acceptMapAddress(data) {
        if (!this._awaitingMap) return false;
        this._awaitingMap = false;

        this.currentFields.contract_address = data.contract_address;
        this.currentFields.mem_basepoint    = data.mem_basepoint;
        this.currentFields.address_type     = data.address_type;
        if (data.place_name) this.currentFields.place_name = data.place_name;

        this._addMsg(`선택: ${data.contract_address}`, 'me');
        this.chatHistory.push({ role: 'user', text: data.contract_address });
        this._finishEdit('contract_address');
        this._afterFieldsChanged();
        this._refreshQuickReplies();
        return true;
    }

    /**
     * work_time 은 날짜+시각을 합친 값("YYYY-MM-DD HH:MM:00")이라 둘 다 있어야 만들 수 있다.
     * 날짜·시간을 버튼으로 따로 고르는 경우, 나중에 고른 쪽에서 이 함수가 둘을 합쳐준다.
     * (AI 가 문장에서 한 번에 뽑을 때는 서버가 이미 합쳐서 내려주므로 이 경로를 타지 않는다)
     */
    _syncWorkTime() {
        const d = this.currentFields.work_day;
        const h = this.currentFields.work_start_h;
        if (!d || h === undefined || h === null || h === '') return;

        const hh = String(h).padStart(2, '0');
        const mm = String(this.currentFields.work_start_m ?? 0).padStart(2, '0');
        this.currentFields.work_time  = `${d} ${hh}:${mm}:00`;
        this.currentDisplay.work_time = this._timeKo(Number(hh), Number(mm));
    }

    /**
     * 24시간 시각 → 화면용 "오전/오후 h:mm". 서버 formatTimeKo 와 같은 규칙으로 맞춘다.
     * (버튼으로 시간을 고른 경우엔 서버를 안 거치므로 여기서도 같은 모양을 만들어야 한다)
     */
    _timeKo(h, m) {
        const ampm = h < 12 ? '오전' : '오후';
        const h12  = (h % 12) || 12;
        return `${ampm} ${h12}:${String(m).padStart(2, '0')}`;
    }

    /**
     * 도착시각이 AI 발주 자동 긴급 구간인지 — 확인 카드에 미리 보여주기 위한 표시용.
     *
     * 최종 판정은 서버(OrderPolicyService::isAiQuickTime)가 하고 그 값이 발주 폼까지 간다.
     * 구간의 정본도 거기다 — G5 로 내려온 값을 읽어서 쓴다(ClientBootstrap::codes()).
     * 발주 폼의 수동 긴급(Baljoo.js isQuickTime)은 10~60분 그대로다.
     */
    _isQuickTime() {
        if (!this.currentFields.work_time) return false;
        const after = dayjs(this.currentFields.work_time).diff(dayjs(), 'minute');
        return after >= G5['AI_QUICK_MIN'] && after <= G5['AI_QUICK_MAX'];
    }

    async _commit() {
        const $cta = $('#' + this.id).find('.ai-sum-cta');

        const fields = { ...this.currentFields };

        $cta.prop('disabled', true).text('이동 중…');
        try {
            const res = await fetch('/m/ajax/bal/ai/commit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                body: JSON.stringify({ fields }),
            });
            const data = await res.json();
            if (data.ok && data.redirect) {
                location.replace(data.redirect);
            } else {
                this._addMsg(data.error || '발주 폼 이동에 실패했습니다.', 'bot err');
                $cta.prop('disabled', false).text('발주하기');
            }
        } catch (err) {
            this._addMsg('네트워크 오류: ' + err.message, 'bot err');
            $cta.prop('disabled', false).text('발주하기');
        }
    }
}
